<template>
  <div>
    <doe-label :name="name">
      {{ label }}
    </doe-label>
    <div class="relative max-w-3xl">
      <div class="sr-only" aria-live="assertive">
        <template v-if="results.length >= 1">
        {{ results.length }} suggestions found, to navigate use up and down arrows
        </template>
      </div>
      <div 
        class="search-container bg-gray-300 border border-gray-800 rounded cursor-text flex flex-wrap justify-start content-around relative"
        :class="{ 'rounded-b-none': query.length >= queryThreshold }"
      >
        <ul v-if="saved.length" class="pb-1">
          <li 
            class="inline-block"
            v-for="(option, index) in saved"
            :key="index"
          >
            <button
              @click.prevent="removeOption(option)"
              class="cursor-pointer inline-block text-blue-900 font-sans font-semibold select-none py-2 px-3 rounded border-2 text-blue-900 bg-gray-400 border-blue-900 leading-snug py-2 min-w-sm text-base mt-1 ml-1"
            >
              {{ option.School_name }} <span class="sr-only">Remove</span><span aria-hidden="true"><doe-icon icon="times" size="small" class="ml-1" /></span>
            </button>
          </li>
        </ul>
        <input
          v-if="!saved.length >= 1"
          :id="name"
          :name="name"
          ref="search"
          type="search"
          autocomplete="off"
          v-model="query"
          @keydown="handleKeypress($event)"
          role="combobox"
          aria-owns="res"
          aria-autocomplete="both"
          :aria-activedescendant="highlighted !== false ? `suggestion-${highlighted}` : false"
          :aria-expanded="results.length >= 1 ? true : false"
          :aria-describedby="`${name}Hint`"
        >
      </div>
      <div v-show="query.length >= queryThreshold" class="absolute w-full">
        <div v-show="results.length >= 1" class="bg-white" id="res" role="listbox" tabindex="-1">
          <div
            v-for="(option, index) in results"
            :key="index"
            role="option"
            tabindex="-1"
            :id="`suggestion-${index}`"
            class="bg-white border border-blue-900 border-t-0 font-sans font-light text-base text-black leading-loose px-4 py-3 w-full cursor-pointer"
            :class="{ 'bg-gray-400': highlighted === index }"
            :aria-selected="highlighted === index ? true : false"
            @click="handleClick(option)"
          >
            {{ option.School_name }}
          </div>
        </div>
      </div>
      <doe-hint v-if="hint" :hint="hint" :id="`${name}Hint`" />
      <doe-error v-if="errors.has('sendSchoolName')">
        {{ errors.first("sendSchoolName") }}
      </doe-error>
    </div>
  </div>
</template>

<script>
import { serviceCallWithAuth } from "@/helpers/cognito";
import DoeLabel from "@/components/field/Label";
import DoeHint from "@/components/field/Hint";
import DoeIcon from "@/components/icon/Icon";
import DoeError from "@/components/field/Error";

export default {
  name: "DoeSchool",
  inject: {
    $validator: "$validator"
  },
  props: {
    name: {
      type: String,
      required: true
    },
    value: {
      type: Object
    },
    label: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: "text",
      validator: value => {
        return ["text", "password", "number"].indexOf(value) !== -1;
      }
    },
    validation: {
      type: [Object, String]
    },
    placeholder: {
      type: String
    },
    hint: {
      type: [Array, String]
    }
  },
  data() {
    return {
      schools: [],
      query: '',
      queryThreshold: 3,
      newOptionName: '',
      newOptionEmail: '',
      saved: [],
      highlighted: false,
      createNewOption: false,
      keys: {
        ESC: 27,
        TAB: 9,
        RETURN: 13,
        LEFT: 37,
        UP: 38,
        RIGHT: 39,
        DOWN: 40
      }
    }
  },
  components: {
    DoeLabel,
    DoeHint,
    DoeIcon,
    DoeError
  },
  watch: {
    query: function() {
      if (this.query.length >= this.queryThreshold) {
        let headers = this.$OAuth.buildHttpHeader();
        let body = {
          q: this.query
        };
        let options = {
          path: "/school",
          method: "POST",
          headers: headers,
          body: JSON.stringify(body)
        };
        serviceCallWithAuth("doe-ttss", options).then(school => {
          this.schools = school;
        });
      }
    }
  },
  mounted() {
    if(this.value && Object.keys(this.value).length >= 1) {
      this.saved.push(this.value);
    }
  },
  computed: {
    results() {
      if (this.query.length >= this.queryThreshold) {
        return this.schools.filter(school => {
          return (
            school.School_name.toLowerCase().includes(
              this.query.toLowerCase()
            ) &
            !this.saved.find(s => s.School_Email === school.School_Email) &
            (school.Level_of_schooling !== "Secondary School")
          );
        });
      } else {
        return []; 
      }
    }
  },
  methods: {
    removeOption(option) {
      const index = this.saved.indexOf(option);
      this.saved.splice(index, 1);
    },
    saveOption(option) {
      this.saved.push({
        School_name: option.School_name,
        School_code: option.School_code,
        Street: option.Street,
        Town_suburb: option.Town_suburb,
        Postcode: option.Postcode,
        State: "New South Wales",
        Phone: option.Phone,
        School_Email: option.School_Email
      });
      this.$emit("input", this.saved[0]);
    },
    handleClick(option) {
      this.saveOption(option);
      this.query = '';
      this.highlighted = false; 
      this.$refs.search.focus();
    },
    handleKeypress(event) {
    	switch (event.which) {
        case this.keys.ESC:
          this.highlighted = false;
        break;
        case this.keys.RIGHT:
    			return this.selectOption();
        case this.keys.TAB:
          this.highlighted = false;
        break;
        case this.keys.RETURN:
		      event.preventDefault();
          event.stopPropagation();
    			return this.selectOption();
        case this.keys.UP:
		      event.preventDefault();
          event.stopPropagation();
    			return this.moveUp();
        case this.keys.DOWN:
		      event.preventDefault();
          event.stopPropagation();
    			return this.moveDown();
    		default:
    			return;
      }
    },
    moveUp() {
      if(this.results.length === 0) {
        this.highlighted = 'create';
        return;
      }
      switch (this.highlighted) {
        case 0:
        case false:
          this.highlighted = 'create';
        break;
        case 'create':
          this.highlighted = this.results.length - 1;
        break;
    		default:
          this.highlighted = this.highlighted - 1;
      }
    },
    moveDown() {
      if(this.results.length === 0) {
        this.highlighted = 'create';
        return;   
      }
      switch (this.highlighted) {
        case false:
        case 'create':
          this.highlighted = 0;
        break;
        case this.results.length - 1:
          this.highlighted = 'create';
        break;
    		default:
          this.highlighted = this.highlighted + 1;
      }
    },
    selectOption() {
      if(this.highlighted === 'create') {
        this.createOption();
        return;
      }
      if(this.highlighted !== false) {
        this.saveOption(this.results[this.highlighted]);
        this.query = '';
        this.highlighted = false; 
      } else {
        return;
      }
    },
    handleModalTab(name, e) {
      if (e.shiftKey) {
        if (name === "newOptionName") {
          this.$refs.close.focus();
        }
        if (name === "close") {
          this.$refs.add.focus();
        }
        if (name === "add") {
          this.$refs.cancel.focus();
        }
        if (name === "cancel") {
          this.$refs.newOptionEmail.focus();
        }
        if (name === "newOptionEmail") {
          this.$refs.newOptionName.focus();
        }
      } else {
        if (name === "newOptionName") {
          this.$refs.newOptionEmail.focus();
        }
        if (name === "newOptionEmail") {
          this.$refs.cancel.focus();
        }
        if (name === "cancel") {
          this.$refs.add.focus();
        }
        if (name === "add") {
          this.$refs.close.focus();
        }
        if (name === "close") {
          this.$refs.newOptionName.focus();
        }
      }      
    },
  }
};
</script>

<style lang="scss" scoped>
input[type="search"] {
  @apply bg-gray-300 font-sans leading-snug m-1 py-2 px-3 subpixel-antialiased text-gray-900 text-xl w-full;
  &:focus {
    outline: none;
  }
}
input[type="text"] {
  @apply bg-gray-300 border border-gray-800 rounded font-sans leading-snug px-3 py-2 subpixel-antialiased text-gray-900 text-xl w-full;
  &:hover {
    @apply border-blue-900;
    box-shadow: 0 0 0 1px #041e42;
  }
  &:focus {
    @apply border-blue-900;
    box-shadow: 0 0 0 1px #041e42;
    outline: 1px solid #041e42;
    outline-offset: 3px;
  }
}
input.hasError {
  border-color: #d0021b;
}
.search-container {
  &:hover {
    box-shadow: 0 0 0 1px #041e42;
  }
  &:focus {
    box-shadow: 0 0 0 1px #041e42;
    outline: 1px solid #041e42;
    outline-offset: 3px;
  }
}


.mask {
  background-color: rgba(0, 0, 0, 0.3);
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 75;
}
.modal {
  @apply absolute bg-white border border-blue-900 rounded p-6 w-full max-w-3xl z-50;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
button {
  @apply cursor-pointer text-blue-900 font-sans font-semibold select-none w-full;
  svg {
    @apply fill-current;
  }
  @screen md {
    @apply w-auto;
  }
}
.appearance-primary,
.appearance-secondary {
  @apply py-2 px-3 rounded border-2 leading-snug py-2 text-base;
}
.appearance-primary {
  @apply text-white bg-red-900 border-red-900;
  &:focus {
    outline: 1px solid #ce0037;
    outline-offset: 3px;
  }
}
.appearance-secondary {
  @apply text-blue-900 bg-gray-400 border-blue-900;
  &:focus {
    outline: 1px solid #041e42;
    outline-offset: 3px;
  }
}
.appearance-square {
  @apply px-0 min-w-auto text-center;
  height: 44px;
  width: 44px;
}
</style>
